import { Injectable } from '@angular/core';

export interface OAuthAccount { accountId: string; userId: string; version?: number; }
export interface OAuthTransaction extends OAuthAccount {
  networkType: string;
  expiresAt: number;
  state: string;
}

const PREFIX = 'salesmax.oauth.launch.v1.';
const MAX_AGE_MS = 15 * 60_000;
const providerId = (id: string) => id.toUpperCase() === 'GMEET_EXTERNAL' ? 'GMEET' : id.toUpperCase();

/** Stores launch identity, never credentials or authorization codes. Callbacks
 * consume a transaction before sending a connection request. Backend exchange
 * authorization remains necessary; browser state is not a server auth boundary. */
export class OAuthTransactions {
  constructor(private storage: Storage, private origin: string,
    private random: Crypto, private now: () => number = Date.now) {}

  prepare(networkType: string, rawUrl: string, account: OAuthAccount): { url: string; transaction: OAuthTransaction } {
    if (!account?.accountId || !account?.userId) throw new Error('Choose an account before connecting.');
    const url = new URL(rawUrl);
    if (url.protocol !== 'https:') throw new Error('The authorization link is invalid. Check again or contact support.');
    const callback = url.searchParams.get('redirect_uri');
    if (!callback) throw new Error('The authorization link is incomplete. Check again or contact support.');
    if (new URL(callback).origin !== this.origin) {
      throw new Error('This authorization link returns to a different SalesMax site. Connect from the matching SalesMax site.');
    }
    const bytes = this.random.getRandomValues(new Uint8Array(32));
    const state = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    const transaction = { accountId: account.accountId, userId: account.userId,
      networkType: networkType.toUpperCase(), expiresAt: this.now() + MAX_AGE_MS, state };
    this.storage.setItem(PREFIX + state, JSON.stringify(transaction));
    url.searchParams.set('state', state);
    return { url: url.toString(), transaction };
  }

  consume(state: unknown, networkType: string, account: OAuthAccount): OAuthTransaction {
    if (typeof state !== 'string' || !/^[a-f0-9]{64}$/.test(state)) {
      throw new Error('This authorization was not started here. Start the connection again.');
    }
    const raw = this.storage.getItem(PREFIX + state);
    if (!raw) throw new Error('This authorization has expired or was already used. Start the connection again.');
    this.storage.removeItem(PREFIX + state);
    let transaction: OAuthTransaction;
    try { transaction = JSON.parse(raw); } catch { throw new Error('Start the connection again.'); }
    if (transaction.state !== state || !Number.isFinite(transaction.expiresAt) || transaction.expiresAt <= this.now()
      || providerId(transaction.networkType || '') !== providerId(networkType)
      || transaction.accountId !== account?.accountId || transaction.userId !== account?.userId) {
      throw new Error('The account or authorization has changed. Start the connection again for the current account.');
    }
    return transaction;
  }

  cancel(state: string): void { this.storage.removeItem(PREFIX + state); }

  cancelForAccount(accountId: string): void {
    for (let i = this.storage.length - 1; i >= 0; i--) {
      const key = this.storage.key(i);
      if (!key?.startsWith(PREFIX)) continue;
      try {
        const item = JSON.parse(this.storage.getItem(key) || 'null');
        if (!item || item.accountId === accountId || item.expiresAt <= this.now()) this.storage.removeItem(key);
      } catch { this.storage.removeItem(key); }
    }
  }
}

@Injectable({ providedIn: 'root' })
export class OAuthTransactionService extends OAuthTransactions {
  constructor() {
    // Access storage only inside guarded launch/callback operations. Blocking
    // storage must not crash the whole integrations page during DI construction.
    const storage: Storage = {
      get length() { return window.localStorage.length; },
      key: index => window.localStorage.key(index),
      getItem: key => window.localStorage.getItem(key),
      setItem: (key, value) => window.localStorage.setItem(key, value),
      removeItem: key => window.localStorage.removeItem(key),
      clear: () => window.localStorage.clear(),
    };
    super(storage, window.location.origin, window.crypto);
  }

  override cancel(state: string): void { try { super.cancel(state); } catch { /* Callbacks also require readable storage. */ } }
  override cancelForAccount(accountId: string): void { try { super.cancelForAccount(accountId); } catch { /* Fail-closed callback validation. */ } }
}

/** Only documented callback metadata is forwarded; account/user/provider/code
 * are supplied by the validated launch, not arbitrary query parameters. */
export function safeOAuthCallbackParams(params: Record<string, unknown>, networkType = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of ['scope', 'state']) {
    if (typeof params[key] === 'string') out[key] = params[key] as string;
  }
  if (networkType.toUpperCase() === 'SHOPIFY') {
    // The backend must verify Shopify's HMAC. Keep signed callback metadata
    // verbatim; account/operator/code still come from the validated launch.
    const formats = { shop: /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i,
      hmac: /^[a-f0-9]{64}$/i, host: /^[A-Za-z0-9+/_=-]{1,2048}$/, timestamp: /^\d{1,16}$/ };
    for (const [key, format] of Object.entries(formats)) {
      if (typeof params[key] === 'string' && format.test(params[key] as string)) out[key] = params[key] as string;
    }
  }
  if (typeof params['location'] === 'string' && /^(us|eu|in|jp|au|ca|cn|sa|uk)$/i.test(params['location'])) {
    out['location'] = params['location'];
  }
  if (typeof params['accounts-server'] === 'string') {
    try {
      const server = new URL(params['accounts-server']);
      if (server.protocol === 'https:' && /^(accounts\.zoho\.(com|eu|in|jp|com\.au|ca|com\.cn|sa|uk)|accounts\.zohocloud\.ca)$/.test(server.hostname)
        && !server.username && !server.password && !server.port && !server.search && !server.hash && server.pathname === '/') {
        out['accounts-server'] = server.origin;
      }
    } catch { /* Reject unrecognized regional metadata. */ }
  }
  return out;
}
